import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import { meetingsService } from "@/services";
import type {
  MeetingDto,
  MeetingRequestTemplate,
  MeetingDashboardState,
  MeetingType,
} from "@shared/types/MeetingDto";
import type { MeetingsStatsResponse } from "@shared/types/payloads/MeetingsStatsResponse";
import type { CreateMeetingActionPayload } from "@shared/types/payloads/CreateMeetingActionPayload";
import type { CreateMeetingsRequestPayload } from "@shared/types/payloads/CreateMeetingsRequestPayload";
import type { MeetingsUpdatePayload } from "@shared/types/payloads/MeetingsUpdatePayload";

interface MeetingsContextType {
  meetings: MeetingDto[];
  stats: MeetingsStatsResponse | null;
  templates: MeetingRequestTemplate[];
  isLoading: boolean;
  isStatsLoading: boolean;
  error: string | null;
  dashboardState: MeetingDashboardState;
  selectedMeeting: MeetingDto | null;
  isActionLoading: boolean;
  setDashboardState: (state: Partial<MeetingDashboardState>) => void;
  setSelectedMeeting: (meeting: MeetingDto | null) => void;
  fetchMeetings: () => Promise<void>;
  fetchStats: () => Promise<void>;
  fetchTemplates: () => Promise<void>;
  createMeeting: (payload: CreateMeetingsRequestPayload) => Promise<MeetingDto | undefined>;
  performAction: (meetingId: string, payload: CreateMeetingActionPayload) => Promise<MeetingDto | undefined>;
  updateMeeting: (meetingId: string, payload: MeetingsUpdatePayload) => Promise<MeetingDto | undefined>;
  filteredMeetings: MeetingDto[];
}

const MeetingsContext = createContext<MeetingsContextType | null>(null);

const TAB_STATUS_MAP: Partial<Record<MeetingDashboardState["activeTab"], string[]>> = {
  UPCOMING: ["CONFIRMED"],
  PENDING: ["AWAITING_BOOKING"],
  NO_SHOW: ["NO_SHOW"],
};

function filterMeetings(
  meetings: MeetingDto[],
  state: MeetingDashboardState
): MeetingDto[] {
  let filtered = [...meetings];

  const statuses = TAB_STATUS_MAP[state.activeTab];
  if (statuses) {
    filtered = filtered.filter((m) => statuses.includes(m.status));
  }

  if (state.filterByType && state.filterByType !== "ALL") {
    const typeFilter = state.filterByType as MeetingType;
    filtered = filtered.filter((m) => m.type === typeFilter);
  }

  if (state.searchQuery) {
    const q = state.searchQuery.toLowerCase();
    filtered = filtered.filter(
      (m) =>
        m.displayName?.toLowerCase().includes(q) ||
        m.title.toLowerCase().includes(q) ||
        m.email?.toLowerCase().includes(q) ||
        m.company?.toLowerCase().includes(q)
    );
  }

  return filtered;
}

export function MeetingsProvider({ children }: { children: ReactNode }) {
  const [meetings, setMeetings] = useState<MeetingDto[]>([]);
  const [stats, setStats] = useState<MeetingsStatsResponse | null>(null);
  const [templates, setTemplates] = useState<MeetingRequestTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMeeting, setSelectedMeeting] = useState<MeetingDto | null>(
    null
  );
  const [dashboardState, setDashboardStateRaw] =
    useState<MeetingDashboardState>({
      activeTab: "UPCOMING",
      searchQuery: "",
      filterByType: "ALL",
    });

  const setDashboardState = useCallback(
    (partial: Partial<MeetingDashboardState>) => {
      setDashboardStateRaw((prev) => ({ ...prev, ...partial }));
    },
    []
  );

  const fetchMeetings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await meetingsService.getMeetings();
      setMeetings(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch meetings"
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    setIsStatsLoading(true);
    try {
      const data = await meetingsService.getStats();
      setStats(data);
    } catch {
      // stats are non-critical, silently fail
    } finally {
      setIsStatsLoading(false);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const data = await meetingsService.getTemplates();
      setTemplates(data);
    } catch {
      // templates are non-critical
    }
  }, []);

  const createMeeting = useCallback(
    async (payload: CreateMeetingsRequestPayload) => {
      setIsActionLoading(true);
      try {
        const res = await meetingsService.createMeeting(payload);
        if (res.success && res.data) {
          setMeetings((prev) => [res.data!, ...prev]);
          await fetchStats();
          return res.data;
        }
        throw new Error(res.message || "Failed to create meeting");
      } finally {
        setIsActionLoading(false);
      }
    },
    [fetchStats]
  );

  const performAction = useCallback(
    async (meetingId: string, payload: CreateMeetingActionPayload) => {
      setIsActionLoading(true);
      try {
        const res = await meetingsService.performAction(meetingId, payload);
        if (res.success && res.data) {
          setMeetings((prev) =>
            prev.map((m) => (m.id === meetingId ? res.data! : m))
          );
          if (selectedMeeting?.id === meetingId) {
            setSelectedMeeting(res.data);
          }
          await fetchStats();
          return res.data;
        }
        throw new Error(res.message || "Action failed");
      } finally {
        setIsActionLoading(false);
      }
    },
    [fetchStats, selectedMeeting]
  );

  const updateMeeting = useCallback(
    async (meetingId: string, payload: MeetingsUpdatePayload) => {
      setIsActionLoading(true);
      try {
        const res = await meetingsService.updateMeeting(meetingId, payload);
        if (res.success && res.data) {
          setMeetings((prev) =>
            prev.map((m) => (m.id === meetingId ? res.data! : m))
          );
          if (selectedMeeting?.id === meetingId) {
            setSelectedMeeting(res.data);
          }
          await fetchStats();
          return res.data;
        }
        throw new Error(res.message || "Update failed");
      } finally {
        setIsActionLoading(false);
      }
    },
    [fetchStats, selectedMeeting]
  );

  const filteredMeetings = filterMeetings(meetings, dashboardState);

  useEffect(() => {
    fetchMeetings();
    fetchStats();
    fetchTemplates();
  }, [fetchMeetings, fetchStats, fetchTemplates]);

  return (
    <MeetingsContext.Provider
      value={{
        meetings,
        stats,
        templates,
        isLoading,
        isStatsLoading,
        error,
        dashboardState,
        selectedMeeting,
        isActionLoading,
        setDashboardState,
        setSelectedMeeting,
        fetchMeetings,
        fetchStats,
        fetchTemplates,
        createMeeting,
        performAction,
        updateMeeting,
        filteredMeetings,
      }}
    >
      {children}
    </MeetingsContext.Provider>
  );
}

export function useMeetings() {
  const context = useContext(MeetingsContext);
  if (!context) {
    throw new Error("useMeetings must be used within a MeetingsProvider");
  }
  return context;
}
